import { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Input, ProgressBar, Select, Stack, Text } from '@ui'
import { getJSON } from '../api.js'
import { MATCH_SLACK_MS, findDuplicate, matchWalk, offsetFromWalk, walksOnDay } from '../photoMatch.js'
import { formatDate, formatDistance, formatTakenAt, formatTime, sourceLabel, ukDateKey } from '../format.js'
import { useUnit } from '../units.jsx'
import { ACCEPT, contentTypeOf, readTakenAt, uploadToWalk } from '../upload.js'

// "Add photos or videos" on the photos page (2026-10-05): pick many files at once (a day's or a
// week's camera roll), and each goes to the walk it was taken during, by its file time
// (photoMatch.js). Nothing is uploaded until the list has been looked over: files taken outside
// any walk, with no time, already on the site, or deleted from it before are skipped unless a
// walk is picked for them.
// The uploads themselves are the walk page's (upload.js), one file at a time.

const minutes = (ms) => {
  const m = Math.round(Math.abs(ms) / 60000)
  return m === 1 ? '1 min' : `${m} min`
}

// What a row's line under the file name says about where it's going.
function rowNote(row, walk) {
  const already = (row.duplicate && `already on the site (${formatDate(row.duplicate.start_time)})`) ||
    (row.deletedBefore && `${row.deletedBefore.purged ? 'permanently ' : ''}deleted from the site on ${formatDate(row.deletedBefore.deleted_at)} (it was on the walk of ${formatDate(row.deletedBefore.start_time)})`)
  if (!walk) {
    if (already) return `Skipped: ${already}`
    if (!row.takenAt) return 'No time in the file: pick a day and walk, or leave it out'
    return 'Not taken during a walk: left out unless you pick one'
  }
  let note = 'No time in the file'
  if (row.takenAt) {
    const offset = offsetFromWalk(Date.parse(row.takenAt), walk)
    note = offset === 0 ? 'Taken during this walk' : `Taken ${minutes(offset)} ${offset < 0 ? 'before' : 'after'} this walk`
    if (Math.abs(offset) > MATCH_SLACK_MS) note = `Taken outside this walk (${formatTakenAt(row.takenAt, walk.start_time)})`
  }
  return already ? `${note}; ${already}` : note
}

// A small preview of a photo, made one at a time so a big batch doesn't hold every full-size
// image in memory. Videos get a plain tile.
async function smallPreview(file) {
  const bitmap = await createImageBitmap(file, { resizeWidth: 96, resizeQuality: 'medium', imageOrientation: 'from-image' })
  const canvas = document.createElement('canvas')
  canvas.width = bitmap.width
  canvas.height = bitmap.height
  canvas.getContext('2d').drawImage(bitmap, 0, 0)
  bitmap.close()
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.7))
  return blob ? URL.createObjectURL(blob) : null
}

export default function BulkUpload({ photos, onAdded }) {
  const [unit] = useUnit()
  const input = useRef(null)
  const [walks, setWalks] = useState(null)
  const [phase, setPhase] = useState('idle') // idle | reading | review | uploading | done
  const [reading, setReading] = useState({ done: 0, total: 0 })
  const [rows, setRows] = useState([])
  const [error, setError] = useState(null)
  const batch = useRef(0) // bumped per pick, so a stale preview loop stops

  const update = (key, change) => setRows((list) => list.map((r) => (r.key === key ? { ...r, ...change } : r)))
  const walkById = (id) => walks?.find((w) => w.id === id) ?? null

  const clear = () => {
    batch.current++
    setRows((list) => { list.forEach((r) => r.preview && URL.revokeObjectURL(r.preview)); return [] })
    setPhase('idle')
    setError(null)
  }
  useEffect(() => () => { batch.current++ }, [])

  // Leaving the page mid-upload would stop it.
  useEffect(() => {
    if (phase !== 'uploading') return undefined
    const warn = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [phase])

  const pick = async (files) => {
    clear()
    const mine = ++batch.current
    setPhase('reading')
    setReading({ done: 0, total: files.length })
    try {
      const [allWalks, deleted] = await Promise.all([walks ?? getJSON('/api/walks'), getJSON('/api/photos/deleted')])
      setWalks(allWalks)
      const out = []
      for (const [n, file] of files.entries()) {
        const contentType = contentTypeOf(file)
        const usable = contentType.startsWith('image/') || contentType.startsWith('video/')
        const takenAt = usable ? await readTakenAt(file) : null
        if (batch.current !== mine) return
        const duplicate = usable ? findDuplicate(file, takenAt, photos) : null
        // A file deleted from the site isn't added back unless a walk is picked for it on purpose.
        const deletedBefore = usable && !duplicate ? findDuplicate(file, takenAt, deleted) : null
        const match = usable ? matchWalk(takenAt, allWalks) : null
        out.push({
          key: `${mine}-${n}`, file, name: file.name, usable, takenAt, duplicate, deletedBefore,
          isVideo: contentType.startsWith('video/'),
          day: takenAt ? ukDateKey(takenAt) : '',
          walkId: match && !duplicate && !deletedBefore ? match.id : null,
          stage: null, progress: 0, error: usable ? null : 'Not a photo or video',
        })
        setReading({ done: n + 1, total: files.length })
      }
      out.sort((a, b) => (a.takenAt ?? '9').localeCompare(b.takenAt ?? '9'))
      setRows(out)
      setPhase('review')
      for (const row of out) {
        if (row.isVideo || !row.usable) continue
        const preview = await smallPreview(row.file).catch(() => null)
        if (batch.current !== mine) { if (preview) URL.revokeObjectURL(preview); return }
        if (preview) update(row.key, { preview })
      }
    } catch (err) {
      if (batch.current === mine) { setError(err.message); setPhase('idle') }
    }
  }

  const chooseDay = (row, day) => {
    const counted = walksOnDay(day, walks).filter((w) => w.is_counted)
    update(row.key, { day, walkId: counted.length === 1 ? counted[0].id : null })
  }

  const upload = async () => {
    setPhase('uploading')
    const mine = batch.current
    for (const row of rows.filter((r) => r.walkId)) {
      if (batch.current !== mine) return
      try {
        const { warning } = await uploadToWalk(row.walkId, row.file, {
          onStage: (stage) => update(row.key, { stage }),
          onProgress: (progress) => update(row.key, { progress }),
        })
        update(row.key, { stage: 'Added', progress: 1, warning })
        onAdded?.()
      } catch (err) {
        update(row.key, { error: err.message })
      }
    }
    setPhase('done')
  }

  const chosen = rows.filter((r) => r.walkId)
  const added = rows.filter((r) => r.stage === 'Added').length
  const failed = chosen.filter((r) => r.error).length

  return (
    <>
      <input
        ref={input} type="file" accept={ACCEPT} multiple hidden
        onChange={(e) => { if (e.target.files.length) pick([...e.target.files]); e.target.value = '' }}
      />
      <Button size="sm" variant="secondary" outline onClick={() => input.current.click()} disabled={phase === 'reading' || phase === 'uploading'}>
        Add photos or videos
      </Button>
      {(phase !== 'idle' || error) && (
        <Box className="bulk-upload" bordered rounded p={2} mb={3} fullWidth>
          {error && <Alert variant="danger" mb={2}>Couldn't read the files: {error}</Alert>}
          {phase === 'reading' && <Text as="p" text="small" mb={0}>Reading file times… {reading.done} of {reading.total}</Text>}
          {phase !== 'reading' && rows.length > 0 && (
            <>
              <Text as="p" text="small" mb={2}>
                {phase === 'review' && <>Each file goes to the walk it was taken during. Check the list, then upload. </>}
                <strong>{chosen.length} of {rows.length}</strong> to add
                {phase !== 'review' && <>: {added} added{failed ? `, ${failed} failed` : ''}</>}.
              </Text>
              <Box as="ul" unstyledList mb={2}>
                {rows.map((row) => {
                  const walk = walkById(row.walkId)
                  const dayWalks = row.day ? walksOnDay(row.day, walks) : []
                  const locked = phase !== 'review'
                  return (
                    <Stack as="li" key={row.key} className="bulk-upload-row" wrap align="center" gap={2} py={2} dimmed={!walk}>
                      <span className="bulk-upload-thumb" aria-hidden="true">
                        {row.preview ? <img src={row.preview} alt="" /> : <span>{row.isVideo ? 'Video' : 'Photo'}</span>}
                      </span>
                      <Box grow text="small" style={{ minWidth: '12rem', flexBasis: '12rem' }}>
                        <Box truncate>{row.name}</Box>
                        <Box muted>
                          {row.takenAt ? `${formatDate(row.takenAt)}, ${formatTime(row.takenAt)} · ` : ''}
                          {row.usable ? rowNote(row, walk) : row.error}
                        </Box>
                        {row.stage && !row.error && (
                          <Box muted>
                            {row.stage === 'Uploading' ? `Uploading ${Math.round(row.progress * 100)}%` : row.stage}
                            {row.stage === 'Uploading' && <ProgressBar value={row.progress * 100} thin />}
                          </Box>
                        )}
                        {row.usable && row.error && <Box tone="danger">Failed: {row.error}</Box>}
                        {row.warning && <Box tone="warning">{row.warning}</Box>}
                      </Box>
                      {row.usable && (
                        <Stack gap={2} className="bulk-upload-controls">
                          <Input
                            type="date" size="sm" aria-label={`Day for ${row.name}`} value={row.day} disabled={locked}
                            onChange={(e) => chooseDay(row, e.target.value)}
                          />
                          <Select
                            size="sm" aria-label={`Walk for ${row.name}`} value={row.walkId ?? ''} disabled={locked}
                            onChange={(e) => update(row.key, { walkId: e.target.value ? Number(e.target.value) : null })}
                          >
                            <option value="">Leave out</option>
                            {dayWalks.map((w) => (
                              <option key={w.id} value={w.id}>
                                {formatTime(w.start_time)} · {formatDistance(w.distance_meters, unit)} · {sourceLabel(w)}
                                {!w.is_counted && w.group_id ? ' (other recording)' : ''}
                              </option>
                            ))}
                          </Select>
                        </Stack>
                      )}
                    </Stack>
                  )
                })}
              </Box>
              <Stack gap={2}>
                {phase === 'review' && (
                  <>
                    <Button size="sm" onClick={upload} disabled={!chosen.length}>
                      Upload {chosen.length} {chosen.length === 1 ? 'file' : 'files'}
                    </Button>
                    <Button size="sm" variant="link" onClick={clear}>Cancel</Button>
                  </>
                )}
                {phase === 'uploading' && <Text text="small" muted>Keep this page open until it's finished.</Text>}
                {phase === 'done' && <Button size="sm" variant="secondary" outline onClick={clear}>Done</Button>}
              </Stack>
            </>
          )}
        </Box>
      )}
    </>
  )
}
